import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';

import { ToasterService } from './toaster.service';

const POLL_MS = 3000;
const WAIT_MS = 5 * 60 * 1000;

function wakeConfig(): { url: string; key: string } {
  const env = environment as { devWakeUrl?: string; devWakeKey?: string };
  return { url: env.devWakeUrl || '', key: env.devWakeKey || '' };
}

/**
 * Dev-only: ask the always-on Lambda to scale ECS up, then wait until
 * API + Identity Server answer. Uses fetch() so the call never goes through
 * Angular HTTP interceptors (which would 503-loop back into this service).
 */
@Injectable({
  providedIn: 'root'
})
export class DevWakeService {
  private inFlight: Promise<boolean> | null = null;
  private wakingToast: HTMLIonToastElement | null = null;

  constructor(private toaster: ToasterService) { }

  enabled(): boolean {
    const { url, key } = wakeConfig();
    return !!(url && key);
  }

  /**
   * @returns true if ECS was actually started (caller can reload the page
   * they were on). false if the env was already healthy.
   */
  ensureAwake(): Promise<boolean> {
    if (!this.enabled()) {
      return Promise.resolve(false);
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.wakeAndWait().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async wakeAndWait(): Promise<boolean> {
    if (await this.isHealthy()) {
      return false;
    }

    await this.showWakingToast();
    try {
      await this.postWake();
      const deadline = Date.now() + WAIT_MS;
      while (Date.now() < deadline) {
        if (await this.isHealthy()) {
          await this.hideWakingToast();
          await this.toaster.success('Dev environment is ready');
          return true;
        }
        await delay(POLL_MS);
      }
      throw new Error('The dev environment did not become healthy in time. Try again in a minute.');
    } catch (err) {
      await this.hideWakingToast();
      await this.toaster.error(wakeErrorMessage(err));
      throw err;
    }
  }

  private async postWake(): Promise<void> {
    const { url, key } = wakeConfig();
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-store',
      headers: { 'X-Dev-Wake-Key': key }
    });
    if (!res.ok) {
      throw new Error(`Failed to wake the dev environment (${res.status}).`);
    }
  }

  private async isHealthy(): Promise<boolean> {
    const apiHealth = new URL('health', environment.attachmentUrl).toString();
    const authDiscovery = `${environment.oidcConfig.server_host}/.well-known/openid-configuration`;
    const [api, auth] = await Promise.all([this.ping(apiHealth), this.ping(authDiscovery)]);
    return api && auth;
  }

  private async ping(url: string): Promise<boolean> {
    try {
      const res = await fetch(url, { method: 'GET', cache: 'no-store' });
      return res.ok;
    } catch {
      return false;
    }
  }

  private async showWakingToast(): Promise<void> {
    if (this.wakingToast) {
      return;
    }
    this.wakingToast = await this.toaster.sticky(
      'Waking the dev environment… this can take a couple of minutes.'
    );
  }

  private async hideWakingToast(): Promise<void> {
    try {
      await this.wakingToast?.dismiss();
    } finally {
      this.wakingToast = null;
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function wakeErrorMessage(err: unknown): string {
  const text = err instanceof Error ? err.message : '';
  if (text === 'Failed to fetch' || text === 'NetworkError when attempting to fetch resource.') {
    return 'Could not reach the wake service. Check the browser console for a CORS error.';
  }
  return text || 'Failed to wake the dev environment.';
}
